import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { CameraIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { equipmentAPI } from '../../services/api'

/**
 * VH-131: the one photo a machine carries.
 *
 * Machines created by a VH-126 scan arrive with an AI crop. The ~30 that
 * predate scanning have nothing, and short of re-scanning an already-inventoried
 * gym there was no way to give them one.
 *
 * Two things this deliberately does NOT do: run any AI (the machine is already
 * named and placed, so there is nothing to identify, and the upload keeps
 * working when the vision provider is down), and resize in the browser (Reto
 * chose image quality; the server converts and caps).
 *
 * Failure is loud. On gym wifi a silent failure would be indistinguishable from
 * success, and the user would walk away from a machine believing it was done.
 */
export default function EquipmentPhotoActions({ equipment }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const inputRef = useRef(null)
  const [error, setError] = useState(null)
  // VH-131: a 64px thumbnail cannot tell you whether the photo is any good.
  // Reto's whole reason for taking it at the machine is to check it there and
  // retake it if it is blurred or badly framed.
  const [expanded, setExpanded] = useState(false)

  const hasPhoto = Boolean(equipment.photo_url)
  // A scan sets `source_image_url` to the frame the crop came from; the upload
  // path clears it. That is how we know an AI crop is about to be overwritten.
  const isScanCrop = Boolean(equipment.source_image_url)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['equipment'] })
    queryClient.invalidateQueries({ queryKey: ['training-locations'] })
  }

  const upload = useMutation({
    mutationFn: (file) => equipmentAPI.uploadPhoto(equipment.id, file),
    onSuccess: () => {
      setError(null)
      invalidate()
    },
    onError: (err) => {
      setError(
        err?.response?.data?.detail || t('equipment.photo.uploadFailed')
      )
    },
  })

  const remove = useMutation({
    mutationFn: () => equipmentAPI.deletePhoto(equipment.id),
    onSuccess: () => {
      setError(null)
      invalidate()
    },
    onError: (err) => {
      setError(
        err?.response?.data?.detail || t('equipment.photo.removeFailed')
      )
    },
  })

  const choose = () => {
    if (hasPhoto && isScanCrop && !confirm(t('equipment.photo.confirmReplaceScan'))) {
      return
    }
    setError(null)
    inputRef.current?.click()
  }

  const onFile = (event) => {
    const file = event.target.files?.[0]
    // Reset first: picking the SAME file twice after a failure fires no change
    // event otherwise, so a retry would look like a dead button.
    event.target.value = ''
    if (file) upload.mutate(file)
  }

  const busy = upload.isPending || remove.isPending

  return (
    <div className="mt-3" data-testid={`equipment-photo-${equipment.id}`}>
      <div className="flex items-center gap-3">
        {hasPhoto ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label={t('equipment.photo.expand')}
            data-testid={`equipment-photo-expand-${equipment.id}`}
          >
            <img
              src={equipment.photo_url}
              alt={equipment.name_en}
              className="h-16 w-16 rounded object-cover cursor-zoom-in"
              data-testid={`equipment-photo-image-${equipment.id}`}
            />
          </button>
        ) : (
          <div
            className="h-16 w-16 rounded bg-gray-100 flex items-center justify-center"
            data-testid={`equipment-photo-placeholder-${equipment.id}`}
          >
            <CameraIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
          </div>
        )}

        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={choose}
            disabled={busy}
            className="btn-outline btn-sm flex items-center disabled:opacity-50"
            data-testid={`equipment-photo-upload-${equipment.id}`}
          >
            <CameraIcon className="h-4 w-4 mr-1" />
            {upload.isPending
              ? t('equipment.photo.uploading')
              : hasPhoto
                ? t('equipment.photo.replace')
                : t('equipment.photo.add')}
          </button>

          {hasPhoto && (
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={busy}
              className="btn-outline btn-sm flex items-center text-red-600 hover:bg-red-50 disabled:opacity-50"
              data-testid={`equipment-photo-remove-${equipment.id}`}
            >
              <TrashIcon className="h-4 w-4 mr-1" />
              {t('equipment.photo.remove')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <p
          className="mt-2 text-sm text-red-600"
          role="alert"
          data-testid={`equipment-photo-error-${equipment.id}`}
        >
          {error}
        </p>
      )}

      {/*
        The check-it-now view. Full size, dark backdrop, dismissed by the close
        button, a click anywhere outside, or Escape -- on a phone the backdrop
        tap is the one people reach for, and Escape matters on a laptop.
      */}
      {expanded && hasPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={equipment.name_en}
          tabIndex={-1}
          onClick={() => setExpanded(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setExpanded(false) }}
          data-testid={`equipment-photo-lightbox-${equipment.id}`}
        >
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="absolute top-4 right-4 rounded-full bg-surface-container/90 p-2 text-gray-900"
            aria-label={t('common.close')}
            data-testid={`equipment-photo-lightbox-close-${equipment.id}`}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          <img
            src={equipment.photo_url}
            alt={equipment.name_en}
            // Stop the backdrop handler firing when the photo itself is
            // tapped -- zooming in and dismissing must not be the same gesture.
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full rounded object-contain"
            data-testid={`equipment-photo-full-${equipment.id}`}
          />
        </div>
      )}

      {/*
        Both camera and library: shoot at the machine on Sunday, or attach a
        photo taken earlier. `capture` is deliberately absent — setting it
        forecloses the library on mobile.
      */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif"
        onChange={onFile}
        className="hidden"
        data-testid={`equipment-photo-input-${equipment.id}`}
      />
    </div>
  )
}
